import React from 'react'
import styled from 'styled-components'
import { styledVariant } from '../../util'
import { SearchInputProps, StyledSearchInputProps } from './search-input.types'
import { inputStatusStyles } from '../../shared/input-status'
import { Icon } from '../icon'
import { Button } from '../button'
import { Feedback } from '../feedback'
import { Flex } from '../flex'

export const StyledSearchContainer = styled.div<{
  width?: string | number
}>`
  ${({ width }) => `
    position: relative;
    width: 100%;
    ${width && `max-width: ${width};`}
  `}
`

export const StyledIconContainer = styled.div`
  ${({ theme }) => `
    position: absolute;
    z-index: 1;
    display: flex;
    justify-content: center;
    align-items: center;
    height: calc(100% - ${theme.sizes[4]});
    margin: ${theme.sizes[2]};
  `}
`

export const StyledClearButton = styled.div`
  ${({ theme }) =>
    `
      display: flex;
      justify-content: center;
      align-items: center;
      height: calc(100% - 0.4rem);
      background: transparent;
      border-color: transparent;
      position: absolute;
      right: 0%;
      z-index: 1;
      margin: 0.2rem 0.1rem;
    }

    button {
      padding: 0 0.6rem;

      &:focus-visible {
        outline: none;
        border: none;
      }
    }

    svg {
      color: ${theme.colors.gray500};
      width: ${theme.sizes[6]};
      height: ${theme.sizes[6]};
    }
  `}
`

export const StyledSearchInput = styled.input<StyledSearchInputProps>`
  ${styledVariant('$status', inputStatusStyles)};

  ${({ theme, $size, $borderRadiiSize, $noIcon }) =>
    `
    position: relative;
    box-sizing: border-box;
    width: 100%;
    height: ${$size === 'small' ? theme.sizes[10] : '3.25rem'};
    border-radius: ${theme.borderRadii[$borderRadiiSize]};
    outline: none;
    padding: ${$noIcon ? `0  ${theme.sizes[6]}` : `0 ${theme.sizes[10]}`};
    font-size:  ${theme.fontSizes.bodySmall};
    color: ${theme.colors.black};

    ::placeholder {
      color: ${theme.colors.gray600};
    }

    &:disabled {
      background-color: ${theme.colors.gray80};
      outline: none;
      border: none;
    }

    @media only screen and (max-device-width: 600px) {
      -webkit-appearance: none;
      font-size: ${theme.sizes[4]};
    },
  `}
`

export const SearchInput = ({
  status = 'default',
  statusMessage,
  handleClearValue,
  width,
  inputSize = 'small',
  borderRadiiSize = 'medium',
  noIcon = false,
  clearButtonAriaLabel,
  ...props
}: SearchInputProps) => {
  const showStatus = status === 'danger' && statusMessage

  return (
    <Flex direction="column" align="flex-start" w="100%" maw={width} gap={2}>
      <StyledSearchContainer width={width}>
        {noIcon ? null : (
          <StyledIconContainer>
            <Icon name="search" size={6} color="gray500" />
          </StyledIconContainer>
        )}
        {props.value && (
          <StyledClearButton>
            <Button
              data-testid="clear-button"
              endIcon="reject"
              variant="tertiary"
              size="small"
              onClick={handleClearValue}
              aria-label={clearButtonAriaLabel}
            />
          </StyledClearButton>
        )}
        <StyledSearchInput
          type="text"
          {...props}
          $status={status}
          $size={inputSize}
          $borderRadiiSize={borderRadiiSize}
          $noIcon={noIcon}
        />
      </StyledSearchContainer>
      {showStatus && <Feedback variant="critical">{statusMessage}</Feedback>}
    </Flex>
  )
}
